import { CashMoneyIcon } from 'src/features/business-create/icons/business-info/cash-money-icon'
import { CreditScoreIcon } from 'src/features/business-create/icons/business-info/credit-score-icon'
import { DownPaymentIcon } from 'src/features/business-create/icons/business-info/down-payment-icon'
import { EmploymentStatusIcon } from 'src/features/business-create/icons/business-info/employment-status-icon'
import { FlagIcon } from 'src/features/business-create/icons/business-info/flag-icon'
import { LocationIcon } from 'src/features/business-create/icons/business-info/location-icon'
import { MortgageBalanceIcon } from 'src/features/business-create/icons/business-info/mortgage-balance-icon'
import { PreapprovedIcon } from 'src/features/business-create/icons/business-info/preapproved-icon'
import { PriceRangeIcon } from 'src/features/business-create/icons/business-info/price-range-icon'
import { PropertyTypeIcon } from 'src/features/business-create/icons/business-info/property-type-icon'
import { TimeframeIcon } from 'src/features/business-create/icons/business-info/timeframe-icon'
import { VeteranBadgeIcon } from 'src/features/business-create/icons/business-info/veteran-badge-icon'
import { ProfessionEnum } from 'src/shared/features/users/schemas/profession'
import { ClientTypeEnum, LoanPurposeEnum } from 'src/shared/schemas/referrals'

import type { LynkPreferencesResponse } from '../api/use-lynk-preferences'
import type { LynkPreferencesUpdate } from '../api/use-update-lynk-preferences'
import { CalendarAppointmentIcon } from '../icons/calendar-appointment-icon'
import { CheckShieldIcon } from '../icons/check-shield-icon'
import { LeafIcon } from '../icons/leaf-icon'

export enum LynkWorkflow {
  FIRST_CONVERSATION = 'first_conversation',
  PRE_QUALIFY = 'pre_qualify',
  LISTING_APPOINTMENT = 'listing_appointment',
  ESTIMATE_COM = 'estimate_com',
  GRC_REFINANCE = 'grc_refinance',
}

export const LYNK_WORKFLOW_NAMES: Record<LynkWorkflow, string> = {
  [LynkWorkflow.FIRST_CONVERSATION]: 'First conversation',
  [LynkWorkflow.PRE_QUALIFY]: 'Pre-qualify',
  [LynkWorkflow.LISTING_APPOINTMENT]: 'Listing appointment',
  [LynkWorkflow.ESTIMATE_COM]: 'Listing appointment',
  [LynkWorkflow.GRC_REFINANCE]: 'Refinance',
}

export type LynkWorkflowItemData = {
  title: string
  link: string
  lynkPreferenceKey: keyof LynkPreferencesResponse
  lynkPreferenceUpdateKey: keyof LynkPreferencesUpdate
  description: string
  descriptionLO?: string
  lineColor: string
  category: string
  LogoImage?: (props: React.ImgHTMLAttributes<HTMLImageElement>) => React.ReactNode
  icon?: (props: { size: number; color: string }) => React.ReactNode
}

export const LYNK_WORKFLOW_DATA: Record<LynkWorkflow, LynkWorkflowItemData> = {
  [LynkWorkflow.FIRST_CONVERSATION]: {
    title: 'First conversation',
    link: '/lynk-workflows/learn-more/first-conversation',
    lynkPreferenceKey: 'firstConversationWorkflowEnabled',
    lynkPreferenceUpdateKey: 'first_conversation_workflow_enabled',
    description:
      'Lynk collects enough basic information to determine that this lead has the fundamental qualifications to potentially buy a home.',
    descriptionLO: `Lynk's goal is to understand the lead's intent, learn about their situation, and route them to the appropriate workflow.`,
    lineColor: 'rgb(91, 186, 111)',
    category: 'General',
    icon: ({ size, color }) => <LeafIcon size={size} color={color} />,
  } as const,
  [LynkWorkflow.PRE_QUALIFY]: {
    title: 'Pre-qualify',
    link: '/lynk-workflows/learn-more/prequalify',
    lynkPreferenceKey: 'preQualifyWorkflowEnabled',
    lynkPreferenceUpdateKey: 'pre_qualify_workflow_enabled',
    description:
      "Lynk collects enough basic information to determine that this lead is worth the agent or loan officer's time and has the fundamental qualifications to potentially buy a home.",
    lineColor: '#89C2D9',
    category: 'Buy',
    icon: ({ size, color }) => <CheckShieldIcon size={size} color={color} />,
  } as const,
  [LynkWorkflow.LISTING_APPOINTMENT]: {
    title: 'Listing appointment',
    link: '/lynk-workflows/learn-more/listing-appointment',
    lynkPreferenceKey: 'listingAppointmentWorkflowEnabled',
    lynkPreferenceUpdateKey: 'listing_appointment_workflow_enabled',
    description: 'Lynk nurtures the seller lead until they are ready to schedule a listing appointment.',
    lineColor: '#7251B5',
    category: 'Sell',
    icon: ({ size, color }) => <CalendarAppointmentIcon size={size} color={color} />,
  } as const,
  [LynkWorkflow.ESTIMATE_COM]: {
    title: 'Listing appointment',
    link: '/lynk-workflows/learn-more/estimate-com',
    lynkPreferenceKey: 'estimateComWorkflowEnabled',
    lynkPreferenceUpdateKey: 'estimate_com_workflow_enabled',
    description: 'Lynk nurtures the seller lead until they are ready to schedule a listing appointment.',
    lineColor: '#7251B5',
    category: 'Sell',
    LogoImage: (props) => (
      <img
        {...props}
        src={require('../assets/estimate-com.png')}
        style={{ ...props.style, aspectRatio: 85 / 10, objectFit: 'contain' }}
      />
    ),
    icon: ({ size, color }) => <CalendarAppointmentIcon size={size} color={color} />,
  } as const,
  [LynkWorkflow.GRC_REFINANCE]: {
    title: 'Pre-qualify',
    link: '/lynk-workflows/learn-more/grc-refinance',
    lynkPreferenceKey: 'grcRefinanceWorkflowEnabled',
    lynkPreferenceUpdateKey: 'grc_refinance_workflow_enabled',
    description:
      'Lynk reaches out to borrowers when rates drop below their current rate, captures interest, and gathers the info your loan officer needs to run real numbers.',
    lineColor: '#344e41',
    category: 'Refinance',
    LogoImage: (props) => (
      <img
        src={require('../assets/grc.png')}
        style={{ ...props.style, aspectRatio: 339 / 100, objectFit: 'contain' }}
      />
    ),
    icon: ({ size, color }) => <CheckShieldIcon size={size} color={color} />,
  } as const,
} as const

export type LYNK_WORKFLOW_PREQUALIFY_FIELDS =
  | 'property_type'
  | 'desired_locations'
  | 'exact_locations'
  | 'price_range'
  | 'down_payment_estimate'
  | 'timeframe'
  | 'is_pre_approved'
  | 'credit_score_estimate'
  | 'employment_status'
  | 'income_estimate'

export type LYNK_WORKFLOW_LISTING_APPOINTMENT_FIELDS =
  | 'exact_locations'
  | 'estimated_property_value'
  | 'mortgage_balance'
  | 'desired_closing_date'
  | 'known_property_issues'
  | 'appointment_scheduled'

export const LYNK_WORKFLOW_FIELD_LABELS = {
  [LynkWorkflow.PRE_QUALIFY]: {
    property_type: 'Property type',
    desired_locations: 'Desired locations',
    exact_locations: 'Exact locations',
    price_range: 'Price range',
    down_payment_estimate: 'Down payment estimate',
    timeframe: 'Timeframe',
    is_pre_approved: 'Is pre-approved',
    credit_score_estimate: 'Credit score estimate',
    employment_status: 'Employment status',
    income_estimate: 'Income estimate',
    pre_approved_by: 'Pre-approval status and lender',
  },
  [LynkWorkflow.LISTING_APPOINTMENT]: {
    exact_locations: 'Location',
    estimated_property_value: 'Estimated property value',
    mortgage_balance: 'Mortgage balance',
    desired_closing_date: 'Desired closing date',
    known_property_issues: 'Known property issues',
    appointment_scheduled: 'Appointment scheduled',
    appointment_date: 'Preferred appointment date',
  },
  [LynkWorkflow.ESTIMATE_COM]: {
    exact_locations: 'Location',
    estimated_property_value: 'Estimated property value',
    mortgage_balance: 'Mortgage balance',
    desired_closing_date: 'Desired closing date',
    known_property_issues: 'Known property issues',
    appointment_scheduled: 'Appointment scheduled',
    appointment_date: 'Preferred appointment date',
  },
  [LynkWorkflow.GRC_REFINANCE]: {
    property_type: 'Property type',
    residence_type: 'Residence type',
    estimated_property_value: 'Estimated property value',
    mortgage_balance: 'Current mortgage balance',
    cash_out_amount: 'Cash out amount',
    credit_score_estimate: 'Credit score',
    employment_status: 'Employment status',
    income_estimate: 'Income',
    refinance_reason: 'Refinance goal',
    is_veteran: 'Veteran',
    working_with_lender: 'Working with another lender',
    best_time_to_connect: 'Best time to connect',
    is_interested_in_refinance: 'Interested in refinancing',
    mortgage_rate_updated_at: 'Mortgage rate updated at',
  },
} as const

export const BUSINESS_FIELD_LABELS = {
  property_type: 'Property type',
  desired_locations: 'Desired locations',
  exact_locations: 'Exact locations',
  price_range: 'Price range',
  down_payment_estimate: 'Down payment estimate',
  timeframe: 'Timeframe',
  is_pre_approved: 'Is pre-approved',
  credit_score_estimate: 'Credit score estimate',
  employment_status: 'Employment status',
  income_estimate: 'Income estimate',
  pre_approved_by: 'Pre-approval status and lender',
  estimated_property_value: 'Estimated property value',
  mortgage_balance: 'Mortgage balance',
  desired_closing_date: 'Desired closing date',
  known_property_issues: 'Known property issues',
  appointment_scheduled: 'Appointment scheduled',
  appointment_date: 'Preferred appointment date',
} as const

export const LYNK_WORKFLOW_FIELD_ICONS = {
  property_type: <PropertyTypeIcon size={20} className="text-primary" />,
  desired_locations: <LocationIcon size={20} className="text-primary" />,
  exact_locations: <LocationIcon size={20} className="text-primary" />,
  price_range: <PriceRangeIcon size={20} className="text-primary" />,
  down_payment_estimate: <DownPaymentIcon size={20} className="text-primary" />,
  timeframe: <TimeframeIcon size={20} className="text-primary" />,
  is_pre_approved: <PreapprovedIcon size={20} className="text-primary" />,
  credit_score_estimate: <CreditScoreIcon size={20} className="text-primary" />,
  employment_status: <EmploymentStatusIcon size={20} className="text-primary" />,
  income_estimate: <DownPaymentIcon size={20} className="text-primary" />,
  pre_approved_by: <PreapprovedIcon size={20} className="text-primary" />,
  estimated_property_value: <DownPaymentIcon size={20} className="text-primary" />,
  mortgage_balance: <MortgageBalanceIcon size={20} className="text-primary" />,
  desired_closing_date: <TimeframeIcon size={20} className="text-primary" />,
  known_property_issues: <PreapprovedIcon size={20} className="text-primary" />,
  appointment_scheduled: <PreapprovedIcon size={20} className="text-primary" />,
  appointment_date: <TimeframeIcon size={20} className="text-primary" />,
  residence_type: <PropertyTypeIcon size={20} className="text-primary" />,
  cash_out_amount: <CashMoneyIcon size={20} className="text-primary" />,
  refinance_reason: <FlagIcon size={20} className="text-primary" />,
  is_veteran: <VeteranBadgeIcon size={40} className="text-primary" />,
  working_with_lender: <PreapprovedIcon size={20} className="text-primary" />,
  best_time_to_connect: <CalendarAppointmentIcon size={20} className="text-primary" />,
  is_interested_in_refinance: <FlagIcon size={20} className="text-primary" />,
  mortgage_rate_updated_at: <CalendarAppointmentIcon size={20} className="text-primary" />,
} as const

export type LYNK_WORKFLOW_FIELDS = LYNK_WORKFLOW_PREQUALIFY_FIELDS | LYNK_WORKFLOW_LISTING_APPOINTMENT_FIELDS

export const WORKFLOW_FIELD_CONFIGS = {
  [LynkWorkflow.PRE_QUALIFY]: [
    'property_type',
    'desired_locations',
    'exact_locations',
    'price_range',
    'down_payment_estimate',
    'timeframe',
    'is_pre_approved',
    'credit_score_estimate',
    'employment_status',
    'income_estimate',
    'pre_approved_by',
  ] as const,
  [LynkWorkflow.LISTING_APPOINTMENT]: [
    'exact_locations',
    'estimated_property_value',
    'mortgage_balance',
    'desired_closing_date',
    'known_property_issues',
    'appointment_scheduled',
    'appointment_date',
  ] as const,
  [LynkWorkflow.ESTIMATE_COM]: [
    'exact_locations',
    'estimated_property_value',
    'mortgage_balance',
    'desired_closing_date',
    'known_property_issues',
    'appointment_scheduled',
    'appointment_date',
  ] as const,
  [LynkWorkflow.FIRST_CONVERSATION]: [
    // TODO: Add fields for profile lead
    'exact_locations',
    'estimated_property_value',
    'mortgage_balance',
    'desired_closing_date',
    'known_property_issues',
    'appointment_scheduled',
    'appointment_date',
  ] as const,
  [LynkWorkflow.GRC_REFINANCE]: [
    'property_type',
    'residence_type',
    'estimated_property_value',
    'mortgage_balance',
    'cash_out_amount',
    'credit_score_estimate',
    'employment_status',
    'income_estimate',
    'refinance_reason',
    'is_veteran',
    'working_with_lender',
    'best_time_to_connect',
    'is_interested_in_refinance',
    'mortgage_rate_updated_at',
  ] as const,
} as const

export enum FieldCompletionStatus {
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  ATTEMPTED = 'attempted',
  IN_PROGRESS = 'in_progress',
}

export type WorkflowFieldContext = {
  status: FieldCompletionStatus
  extracted_information: string | null
  partial_count: number
  vague_count: number
  resistance_count: number
}

// Get the field names for a specific workflow
type PreQualifyFields = (typeof WORKFLOW_FIELD_CONFIGS)[LynkWorkflow.PRE_QUALIFY][number]
type ListingAppointmentFields = (typeof WORKFLOW_FIELD_CONFIGS)[LynkWorkflow.LISTING_APPOINTMENT][number]
type FirstConversationFields = (typeof WORKFLOW_FIELD_CONFIGS)[LynkWorkflow.FIRST_CONVERSATION][number]
type EstimateComFields = (typeof WORKFLOW_FIELD_CONFIGS)[LynkWorkflow.ESTIMATE_COM][number]
type GrcRefinanceFields = (typeof WORKFLOW_FIELD_CONFIGS)[LynkWorkflow.GRC_REFINANCE][number]

// Map each field to WorkflowFieldContext for each workflow
type PreQualifyData = {
  [K in PreQualifyFields]?: WorkflowFieldContext
}

type ListingAppointmentData = {
  [K in ListingAppointmentFields]?: WorkflowFieldContext
}

type FirstConversationData = {
  [K in FirstConversationFields]?: WorkflowFieldContext
}

type EstimateComData = {
  [K in EstimateComFields]?: WorkflowFieldContext
}

type GrcRefinanceData = {
  [K in GrcRefinanceFields]?: WorkflowFieldContext
}

export type BusinessWorkflowContext = {
  [LynkWorkflow.PRE_QUALIFY]?: PreQualifyData
  [LynkWorkflow.LISTING_APPOINTMENT]?: ListingAppointmentData
  [LynkWorkflow.FIRST_CONVERSATION]?: FirstConversationData
  [LynkWorkflow.ESTIMATE_COM]?: EstimateComData
  [LynkWorkflow.GRC_REFINANCE]?: GrcRefinanceData
}

// BusinessLynkData with workflow-specific field types
export type BusinessLynkData = {
  currentWorkflow: LynkWorkflow
  workflowContext: BusinessWorkflowContext
}

export const BUSINESS_WORKFLOW_PATHS = {
  [ProfessionEnum.REAL_ESTATE_AGENT]: {
    [ClientTypeEnum.BUY]: [LynkWorkflow.FIRST_CONVERSATION, LynkWorkflow.PRE_QUALIFY, LynkWorkflow.LISTING_APPOINTMENT],
    [ClientTypeEnum.SELL]: [LynkWorkflow.FIRST_CONVERSATION, LynkWorkflow.LISTING_APPOINTMENT],
    [ClientTypeEnum.LEASE]: [LynkWorkflow.FIRST_CONVERSATION],
  },
  [ProfessionEnum.MORTGAGE_LOAN_OFFICER]: {
    [LoanPurposeEnum.PURCHASE]: [LynkWorkflow.FIRST_CONVERSATION, LynkWorkflow.PRE_QUALIFY],
    [LoanPurposeEnum.REFINANCE]: [LynkWorkflow.FIRST_CONVERSATION, LynkWorkflow.GRC_REFINANCE],
    [LoanPurposeEnum.OTHER]: [LynkWorkflow.FIRST_CONVERSATION],
  },
} as const
